import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { clientService } from '../services/clientService'
import type { ClientFormData } from '../types'
import { useAuthStore } from '@/modules/auth/stores/authStore'

export function useClientMutation() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatBirthdate = (birthdate: string): string => {
    if (!birthdate) return birthdate
    // Backend expects datetime; append time if only date is provided (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      return `${birthdate}T00:00:00`
    }
    return birthdate
  }

  const create = async (data: ClientFormData) => {
    setIsSubmitting(true)
    setError(null)
    try {
      const payload = {
        ...data,
        birthdate: formatBirthdate(data.birthdate),
        id_company: user?.companyId ?? 0,
      }
      const created = await clientService.create(payload)
      navigate(`/clients/${created.id}`)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        if (err.response?.status === 409) {
          setError('Ya existe un cliente con ese RUT o email')
        } else {
          setError(typeof detail === 'string' ? detail : 'Error al crear cliente')
        }
      } else {
        setError('Error al crear cliente')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const update = async (id: number, data: Partial<ClientFormData>) => {
    setIsSubmitting(true)
    setError(null)
    try {
      const updateData = {
        ...data,
        ...(data.birthdate != null && { birthdate: formatBirthdate(data.birthdate) }),
      }
      await clientService.update(id, updateData)
      navigate(`/clients/${id}`)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        if (err.response?.status === 409) {
          setError('Ya existe un cliente con ese RUT o email')
        } else {
          setError(typeof detail === 'string' ? detail : 'Error al actualizar cliente')
        }
      } else {
        setError('Error al actualizar cliente')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const remove = async (id: number) => {
    setIsSubmitting(true)
    setError(null)
    try {
      await clientService.delete(id)
      navigate('/clients')
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        setError(typeof detail === 'string' ? detail : 'Error al eliminar cliente')
      } else {
        setError('Error al eliminar cliente')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return { create, update, remove, isSubmitting, error }
}
